import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useThreads } from '../context/ThreadsContext';
import AddAccountModal from '../components/AddAccountModal';

const ThreadsCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleCallback, selectAccount, connectThreads } = useThreads();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [alreadyConnected, setAlreadyConnected] = useState(false);
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      if (!isAuthenticated) {
        setError('Please log in first to connect your Threads account');
        setStatus('error');
        return;
      }

      if (errorParam) {
        setError(searchParams.get('error_description') || 'Authorization was denied');
        setStatus('error');
        return;
      }

      if (!code) {
        setError('No authorization code received');
        setStatus('error');
        return;
      }

      try {
        const result = await handleCallback(code, state);

        // Threads returns a single account — auto-select
        setStatus('connecting');
        const sel = await selectAccount(result.tokenRef);
        const wasAlready = !!sel?.alreadyConnected;
        setAlreadyConnected(wasAlready);
        setStatus('success');
        if (!wasAlready) {
          setTimeout(() => navigate('/dashboard'), 1500);
        }
      } catch (err) {
        setError(err.message);
        setStatus('error');
      }
    };

    processCallback();
  }, [searchParams, handleCallback, selectAccount, navigate, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
      <div className="max-w-lg w-full text-center">
        {status === 'processing' && (
          <>
            <div className="text-6xl mb-4 animate-pulse">🔄</div>
            <h1 className="text-2xl font-bold text-ink mb-2">Connecting Threads...</h1>
            <p className="text-ink-muted">Please wait while we connect your account</p>
          </>
        )}

        {status === 'connecting' && (
          <>
            <div className="text-6xl mb-4 animate-pulse">🔄</div>
            <h1 className="text-2xl font-bold text-ink mb-2">Setting up account...</h1>
            <p className="text-ink-muted">Almost there</p>
          </>
        )}

        {status === 'success' && !alreadyConnected && (
          <>
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-2xl font-bold text-ink mb-2">Connected!</h1>
            <p className="text-ink-muted">Your Threads account has been connected successfully</p>
            <p className="text-sm text-ink-muted mt-4">Redirecting to dashboard...</p>
          </>
        )}

        {status === 'success' && alreadyConnected && (
          <>
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-2xl font-bold text-ink mb-2">Done</h1>
            <p className="text-ink-muted">Your Threads account is connected.</p>
            <AddAccountModal
              open={true}
              platform="threads"
              variant="already"
              onRetry={connectThreads}
              onClose={() => navigate('/dashboard')}
            />
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-ink mb-2">Connection Failed</h1>
            <p className="text-danger mb-6">{error}</p>
            <button
              onClick={() => navigate('/connect')}
              className="bg-accent text-accent-ink px-6 py-3 rounded-lg font-medium hover:bg-accent-hover transition min-h-[var(--tap-target)] sm:min-h-0"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ThreadsCallback;
